import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  MagnifyingGlassIcon, 
  ChatBubbleLeftIcon,
  PhoneIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import {
  selectConversations,
  selectIsLoading,
  selectSearchQuery,
  setActiveConversation,
  setSearchQuery
} from '../../store/slices/chatSlice';
import { fetchConversations, searchConversations } from '../../store/thunks/chatThunk';
import NewConversationModal from './NewConversationModal';

const ConversationList = () => {
  const dispatch = useDispatch();
  const conversations = useSelector(selectConversations);
  const isLoading = useSelector(selectIsLoading);
  const searchQuery = useSelector(selectSearchQuery);
  
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);

  useEffect(() => {
    // Load conversations when component mounts
    dispatch(fetchConversations({ page: 1, limit: 50 }));
  }, [dispatch]);

  useEffect(() => {
    // Debounced search
    const timeoutId = setTimeout(() => {
      if (localSearchQuery !== searchQuery) {
        dispatch(setSearchQuery(localSearchQuery));
        
        if (localSearchQuery.trim()) {
          dispatch(searchConversations({ query: localSearchQuery.trim() }));
        } else {
          dispatch(fetchConversations({ page: 1, limit: 50 }));
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [localSearchQuery, searchQuery, dispatch]);

  const handleConversationClick = (conversation) => {
    dispatch(setActiveConversation(conversation._id));
  };

  const getClientDisplayName = (conversation) => {
    if (conversation.client && conversation.client.firstName && conversation.client.lastName) {
      return `${conversation.client.firstName} ${conversation.client.lastName}`;
    }
    
    // Fallback to phone number
    const phoneNumber = conversation.phoneNumbers?.[0];
    if (phoneNumber) {
      return phoneNumber;
    }
    
    return 'Unknown Contact';
  };

  const getInitials = (name) => {
    if (name.includes('@') || name.includes('+') || /^\d/.test(name)) {
      return <PhoneIcon className="h-5 w-5" />;
    }
    
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 0) {
      return days === 1 ? '1 day ago' : `${days} days ago`;
    }
    
    if (hours > 0) {
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    }
    
    const minutes = Math.floor(diff / (1000 * 60));
    if (minutes > 0) {
      return minutes === 1 ? '1 min ago' : `${minutes} mins ago`;
    }
    
    return 'Just now';
  };

  const getMessagePreview = (conversation) => {
    if (!conversation.lastMessage) {
      return 'No messages yet';
    }

    const message = conversation.lastMessage;
    const prefix = message.direction === 'outgoing' ? 'You: ' : '';
    const body = message.body || '';
    
    return `${prefix}${body}`.slice(0, 50) + (body.length > 50 ? '...' : '');
  };

  if (isLoading && conversations.length === 0) {
    return (
      <div className="loading-spinner">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading conversations...</span>
      </div>
    );
  }

  return (
    <div className="conversation-list">
      {/* Search Input */}
      <div className="px-4 py-2">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-2 text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded text-center">
            Development Mode: Safe testing numbers or +1-416-669-6505
          </div>
        )}
        
        {/* New Conversation Button */}
        <button
          onClick={() => setShowNewConversationModal(true)}
          className="w-full mt-2 px-3 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          New Conversation
        </button>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="empty-state">
            <ChatBubbleLeftIcon />
            <h3>No conversations yet</h3>
            <p>SMS conversations will appear here when clients send messages.</p>
          </div>
        ) : (
          conversations.map((conversation) => {
            const displayName = getClientDisplayName(conversation);
            const hasUnread = conversation.unreadCount > 0;
            
            return (
              <div
                key={conversation._id}
                className={`conversation-item ${hasUnread ? 'unread' : ''}`}
                onClick={() => handleConversationClick(conversation)}
              >
                {/* Avatar */}
                <div className="conversation-avatar">
                  {getInitials(displayName)}
                </div>

                {/* Details */}
                <div className="conversation-details">
                  <div className="conversation-name">
                    {displayName}
                  </div>
                  <div className="conversation-preview">
                    {getMessagePreview(conversation)}
                  </div>
                </div>

                {/* Meta */}
                <div className="conversation-meta">
                  <div className="conversation-time">
                    {conversation.lastMessageAt && formatTime(conversation.lastMessageAt)}
                  </div>
                  {hasUnread && (
                    <div className="conversation-unread-count">
                      {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Loading More Indicator */}
      {isLoading && conversations.length > 0 && (
        <div className="px-4 py-2 text-center text-sm text-gray-500">
          Loading more conversations...
        </div>
      )}
      
      {/* New Conversation Modal */}
      {showNewConversationModal && (
        <NewConversationModal
          onClose={() => setShowNewConversationModal(false)}
          onConversationCreated={(conversationId) => {
            setShowNewConversationModal(false);
            dispatch(setActiveConversation(conversationId));
          }}
        />
      )}
    </div>
  );
};

export default ConversationList;